import * as Api from '@ViewModels';
import { addDays, endOfWeek, format, isAfter, isBefore, isSameDay, setHours, setMinutes, startOfDay } from 'date-fns';
import { priorityRank } from './priority';

export type ActionItemBucket = 'overdue' | 'today' | 'tomorrow' | 'thisWeek' | 'later' | 'noDate';

export const BUCKET_ORDER: ActionItemBucket[] = ['overdue', 'today', 'tomorrow', 'thisWeek', 'later', 'noDate'];

export const BUCKET_LABELS: Record<ActionItemBucket, string> = {
	overdue: 'Overdue',
	today: 'Today',
	tomorrow: 'Tomorrow',
	thisWeek: 'This Week',
	later: 'Later',
	noDate: 'No Date',
};

/** Overdue is not a place you can move something to. */
export const DROPPABLE_BUCKETS: ActionItemBucket[] = ['today', 'tomorrow', 'thisWeek', 'later', 'noDate'];

export interface IDueDateValue {
	dueDate: string | null;
	hasDueTime: boolean;
}

export function getDueDate(item: Pick<Api.IActionItem, 'dueDate'>): Date | null {
	if (!item.dueDate) {
		return null;
	}
	const parsed = new Date(item.dueDate);
	return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function isOverdue(item: Pick<Api.IActionItem, 'dueDate' | 'hasDueTime' | 'isCompleted'>, now = new Date()) {
	return !item.isCompleted && bucketForDueDate(getDueDate(item), !!item.hasDueTime, now) === 'overdue';
}

export function bucketForDueDate(due: Date | null, hasDueTime: boolean, now: Date): ActionItemBucket {
	if (!due) {
		return 'noDate';
	}
	if (hasDueTime ? isBefore(due, now) : isBefore(startOfDay(due), startOfDay(now))) {
		return 'overdue';
	}
	if (isSameDay(due, now)) {
		return 'today';
	}
	if (isSameDay(due, addDays(now, 1))) {
		return 'tomorrow';
	}
	if (!isAfter(startOfDay(due), endOfWeek(now, { weekStartsOn: 1 }))) {
		return 'thisWeek';
	}
	return 'later';
}

export function bucketForActionItem(item: Api.IActionItem, now: Date): ActionItemBucket {
	return bucketForDueDate(getDueDate(item), !!item.hasDueTime, now);
}

function compareNullableDates(a: Date | null, b: Date | null): number {
	if (a && b) {
		return a.getTime() - b.getTime();
	}
	if (a) {
		return -1;
	}
	if (b) {
		return 1;
	}
	return 0;
}

export function compareActionItems(a: Api.IActionItem, b: Api.IActionItem): number {
	const byPriority = priorityRank(b.priority) - priorityRank(a.priority);
	if (byPriority !== 0) {
		return byPriority;
	}
	const byDue = compareNullableDates(getDueDate(a), getDueDate(b));
	if (byDue !== 0) {
		return byDue;
	}
	return compareNullableDates(
		a.creationDate ? new Date(a.creationDate) : null,
		b.creationDate ? new Date(b.creationDate) : null
	);
}

export type GroupedActionItems = Record<ActionItemBucket, Api.IActionItem[]>;

export function groupActionItems(items: Api.IActionItem[], now = new Date()): GroupedActionItems {
	const groups: GroupedActionItems = {
		overdue: [],
		today: [],
		tomorrow: [],
		thisWeek: [],
		later: [],
		noDate: [],
	};
	for (const item of items) {
		groups[bucketForActionItem(item, now)].push(item);
	}
	for (const bucket of BUCKET_ORDER) {
		groups[bucket].sort(compareActionItems);
	}
	return groups;
}

/** Move a due date onto another calendar day, keeping the clock time only when the item has one. */
export function moveDueDateToDay(day: Date, current: IDueDateValue): IDueDateValue {
	const currentDue = current.dueDate ? new Date(current.dueDate) : null;
	if (current.hasDueTime && currentDue && !Number.isNaN(currentDue.getTime())) {
		const moved = setMinutes(setHours(startOfDay(day), currentDue.getHours()), currentDue.getMinutes());
		return { dueDate: moved.toISOString(), hasDueTime: true };
	}
	return { dueDate: startOfDay(day).toISOString(), hasDueTime: false };
}

/** The day an item lands on when dropped into a bucket, or null when the bucket is not a drop target. */
export function dueDateForBucket(bucket: ActionItemBucket, current: IDueDateValue, now: Date): IDueDateValue | null {
	switch (bucket) {
		case 'today':
			return moveDueDateToDay(now, current);
		case 'tomorrow':
			return moveDueDateToDay(addDays(now, 1), current);
		case 'thisWeek': {
			const candidate = addDays(now, 2);
			const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
			return moveDueDateToDay(isAfter(startOfDay(candidate), weekEnd) ? addDays(now, 1) : candidate, current);
		}
		case 'later':
			return moveDueDateToDay(addDays(now, 7), current);
		case 'noDate':
			return { dueDate: null, hasDueTime: false };
		default:
			return null;
	}
}

/** The natural-language phrase quick add understands for landing an item in a bucket, or null when there is none. */
export function bucketQuickAddPhrase(bucket: ActionItemBucket, now: Date): string | null {
	switch (bucket) {
		case 'today':
			return 'today';
		case 'tomorrow':
			return 'tomorrow';
		case 'thisWeek': {
			const target = dueDateForBucket('thisWeek', { dueDate: null, hasDueTime: false }, now);
			return target?.dueDate ? format(new Date(target.dueDate), 'EEEE') : null;
		}
		case 'later':
			return 'next week';
		default:
			return null;
	}
}
